import React, { useRef } from "react";
import styled from "styled-components";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import img1 from "../../assets/homeAssets/trImg1.png";
import img2 from "../../assets/homeAssets/trImg2.png";

gsap.registerPlugin(ScrollTrigger);

// --- Styled Components ---

const SectionContainer = styled.section`
  width: 100%;
  overflow: hidden;
  margin: 20em 0 0 0;
`;

const StickyWrapper = styled.div`
  height: 100vh;
  width: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  @media (max-width: 700px) {
    height: 70vh;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  @media (max-width: 500px) {
    height: 70vh;
  }
`;

const BaseImage = styled.div`
  position: absolute;
  inset: 0;
  background-image: url(${(props) => props.$img});
  background-size: cover;
  background-position: center;
  z-index: 1;
`;

const RevealImage = styled.div`
  position: absolute;
  inset: 0;
  background-image: url(${(props) => props.$img});
  background-size: cover;
  background-position: center;
  z-index: 2;
  clip-path: inset(0 0 0 100%);
`;

/* The Aesthetic Scanner Line */
const DividerLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0; /* Starts at the right edge */
  width: 2px;
  background: linear-gradient(
    to bottom,
    transparent,
    rgba(255, 255, 255, 0.8),
    transparent
  );
  box-shadow: 0 0 15px rgba(255, 255, 255, 0.5);
  z-index: 3;
  pointer-events: none;
`;

const TextOverlay = styled.div`
  position: absolute;
  /* Moved to the top and left */
  top: 20%;
  left: 10%;
  /* Adjusted transform to account for new positioning */
  transform: translate(0, 0);
  z-index: 10;
  width: 100%;
  /* Changed text-align to left to match the new position */
  text-align: left;
  font-family: "The Seasons", serif;
  color: #fff;
  pointer-events: none;
  @media (max-width: 800px) {
    top: 12%;
  }
  @media (max-width: 500px) {
    top: 70%;
  }
`;

const Title = styled.h2`
  font-size: 4vw;
  max-width: 500px;
  font-weight: 100;
  margin: 0;
  text-shadow: 0 4px 10px rgb(58, 52, 52);
  @media (max-width: 800px) {
    font-size: 8vw;
  }
  @media (max-width: 500px) {
    font-size: 9vw;
    max-width: 400px;
  }
`;

const Subtitle = styled.p`
  font-size: 1.5vw;
  letter-spacing: 0.2rem;
  /* text-transform: uppercase; */
  /* margin-top: 1rem; */
  opacity: 0.8;
  color: black;
  max-width: 600px;
  text-shadow: 0 1px 1px rgb(10, 10, 10);
  @media (max-width: 800px) {
    font-size: 3vw;
    max-width: 400px;
  }
`;

// --- Component ---

const TransformationSection = () => {
  const sectionRef = useRef();
  const revealImgRef = useRef();
  const lineRef = useRef(); // Ref for the line
  const beforeTextRef = useRef();
  const afterTextRef = useRef();
  const stickyRef = useRef();

  useGSAP(
    () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          // If width <= 500, start when element top hits 3em from viewport top
          // Otherwise, start at "top top" (or your preferred desktop setting)
          start: () => (window.innerWidth <= 500 ? "top 100px" : "top top"),
          end: "+=200%",
          scrub: 1,
          pin: true,
          anticipatePin: 1,
          invalidateOnRefresh: true, // Recalculates if user rotates phone
        },
      });

      gsap.set(afterTextRef.current, { opacity: 0, y: 50 });
      // Line starts at 100% (right edge)
      gsap.set(lineRef.current, { xPercent: 100 });

      tl.to(beforeTextRef.current, {
        opacity: 0,
        y: -50,
        duration: 0.5,
      })
        .to(
          revealImgRef.current,
          {
            clipPath: "inset(0 0 0 0%)",
            ease: "none",
            duration: 1,
          },
          "-=0.2",
        )
        // Moving the line exactly with the clipPath
        .to(
          lineRef.current,
          {
            left: 0,
            xPercent: 0,
            ease: "none",
            duration: 1,
          },
          "<", // Starts at the same time as the clipPath reveal
        )
        .to(afterTextRef.current, {
          opacity: 1,
          y: 0,
          duration: 0.5,
        })
        // Fade out the line at the very end
        .to(lineRef.current, { opacity: 0, duration: 0.2 }, "-=0.2");
    },
    { scope: sectionRef },
  );

  return (
    <SectionContainer ref={sectionRef}>
      <StickyWrapper ref={stickyRef}>
        <ImageContainer>
          <BaseImage $img={img1} />

          <RevealImage ref={revealImgRef} $img={img2}>
            <DividerLine ref={lineRef} />
          </RevealImage>

          {/* Before Text */}
          <TextOverlay ref={beforeTextRef}>
            <Title>Es muss nicht so aussehen.</Title>
            <Subtitle>
              Hautgesundheit ist mehr als ein Trend – sie ist die Basis für
              langfristige Schönheit und sichtbare Qualität.
            </Subtitle>
          </TextOverlay>

          {/* After Text */}
          <TextOverlay ref={afterTextRef}>
            <Title>Entfesseln Sie Ihr wahres Strahlen.</Title>
            <Subtitle>
              Künstlerische Präzision trifft auf klinische Exzellenz.
            </Subtitle>
          </TextOverlay>
        </ImageContainer>
      </StickyWrapper>
    </SectionContainer>
  );
};

export default TransformationSection;
